import React, { useContext, useEffect, useState } from 'react'
import styles from './EnrollmentPeriod.module.css'
import { FormTitle } from '../../../Common/FormTitle'
import { NextButton } from '../../../Common/CtaButton/NextButton'
import { FormField } from '../../../Common/FormField'
import { StepContext } from '../../StepContainer'
import {
    handleKeyDown,
    handleNumericInput,
    validateEmploymentDates,
} from './validate'
import { HandleMonthChangeFunction, HandleYearChangeFunction } from './types'
import { useAbTestGtm } from '../../../../hooks/useAbTestGtm'

export const EnrollmentPeriod = () => {
    const { dataClickLabel, dataViewLabel } = useAbTestGtm()
    const { formState } = useContext(StepContext)
    const [workStartYear, setWorkStartYear] = useState<string>('')
    const [workStartMonth, setWorkStartMonth] = useState<string>('')
    const [workEndYear, setWorkEndYear] = useState<string | null>(null)
    const [workEndMonth, setWorkEndMonth] = useState<string | null>(null)
    const [isFormValid, setIsFormValid] = useState<boolean>(false)
    const [isTenure, setIsTenure] = useState<boolean>(false)
    const [startDateError, setStartDateError] = useState<string>('')
    const [endDateError, setEndDateError] = useState<string>('')
    const [hasInteracted, setHasInteracted] = useState<boolean>(false)

    useEffect(() => {
        if (formState.situation === 'tenure') {
            setIsTenure(true)
            setWorkEndYear('')
            setWorkEndMonth('')
            return
        }
        setIsTenure(false)
    }, [formState.situation])

    useEffect(() => {
        if (hasInteracted) {
            const validationResult = validateEmploymentDates({
                workStartYear,
                workStartMonth,
                workEndYear,
                workEndMonth,
                isTenure,
            })

            setIsFormValid(validationResult.isValid)
            setStartDateError(validationResult.startDateError)
            setEndDateError(validationResult.endDateError)
        } else if (
            workStartYear !== '' ||
            workStartMonth !== '' ||
            (workEndYear !== null && workEndYear !== '') ||
            (workEndMonth !== null && workEndMonth !== '')
        ) {
            setHasInteracted(true)
        }
    }, [
        workStartYear,
        workStartMonth,
        workEndYear,
        workEndMonth,
        isTenure,
        hasInteracted,
    ])

    const handleStartYearChange: HandleYearChangeFunction = value => {
        handleNumericInput(value, setWorkStartYear)
        if (!hasInteracted) setHasInteracted(true)
    }

    const handleEndYearChange: HandleYearChangeFunction = value => {
        handleNumericInput(value, setWorkEndYear)
        if (!hasInteracted) setHasInteracted(true)
    }

    const handleMonthChange: HandleMonthChangeFunction = (value, setMonth) => {
        const numericValue = value.replace(/[^0-9]/g, '')

        const normalizedValue =
            numericValue.length > 2
                ? numericValue.replace(/^0+/, '')
                : numericValue

        if (normalizedValue === '' || parseInt(normalizedValue, 10) <= 12) {
            setMonth(normalizedValue)
            if (!hasInteracted) setHasInteracted(true)
        }
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.selectionWrapper}>
                <div className={styles.monthbox}>
                    <FormTitle
                        formTitleText='在職期間はどれくらいですか？'
                        dataViewLabel={dataViewLabel('EF_StepForm_在職期間')}
                        dataClickLabel={dataClickLabel('EF_StepForm_在職期間-戻る')}
                    />
                </div>
                <div>
                    <div className={styles.nameField}>
                        <FormField
                            label='勤務開始年'
                            placeholder='年'
                            value={workStartYear}
                            isInputType={'tel'}
                            onChange={handleStartYearChange}
                            onKeyDown={handleKeyDown}
                        />
                        <FormField
                            label='勤務開始月'
                            placeholder='月'
                            value={workStartMonth}
                            isInputType={'tel'}
                            onChange={value =>
                                handleMonthChange(value, setWorkStartMonth)
                            }
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                    <div className={styles.errorMessagesWrapper}>
                        {hasInteracted && startDateError && (
                            <p className={styles.errorMessages}>
                                {startDateError}
                            </p>
                        )}
                    </div>
                </div>
                <div>
                    <div className={styles.furiganaField}>
                        <FormField
                            label='勤務終了年'
                            placeholder='年'
                            value={workEndYear || ''}
                            isInputType={'tel'}
                            onChange={handleEndYearChange}
                            onKeyDown={handleKeyDown}
                            disabled={isTenure}
                        />
                        <FormField
                            label='勤務終了月'
                            placeholder='月'
                            isInputType={'tel'}
                            value={workEndMonth || ''}
                            onChange={value =>
                                handleMonthChange(value, setWorkEndMonth)
                            }
                            onKeyDown={handleKeyDown}
                            disabled={isTenure}
                        />
                    </div>
                    <div className={styles.errorMessagesWrapper}>
                        {hasInteracted && !isTenure && endDateError && (
                            <p className={styles.errorMessages}>
                                {endDateError}
                            </p>
                        )}
                    </div>
                </div>
                <NextButton
                    formKey={[
                        'userResumesAttributes',
                        'workStartYear',
                        'workStartMonth',
                        'workEndDateYear',
                        'workEndDateMonth',
                    ]}
                    formValue={[
                        0,
                        workStartYear,
                        workStartMonth,
                        workEndYear,
                        workEndMonth,
                    ]}
                    displayFormKey={['workStartDate', 'workEndDate']}
                    displayFormValue={[
                        `${workStartYear}年${workStartMonth}月`,
                        formState.situation === 'tenure'
                            ? '在職中'
                            : `${workEndYear}年${workEndMonth}月`,
                    ]}
                    isDisabled={!isFormValid}
                    dataClickLabel={dataClickLabel('EF_StepForm_在職期間-次へ')}
                />
            </div>
        </div>
    )
}
